import {
  Avatar,
  Box,
  Card,
  Container,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { BusinessCenter, Check, Close, Edit, Note } from "@material-ui/icons";
import ConfirmationButton from "components/ConfirmationButton";
import Loading from "components/Loading";
import queryClient from "config/query";
import { RootState } from "config/store";
import supportedLocales from "config/supportedLocales";
import { COLORS } from "config/theme";
import { format } from "date-fns";
import i18n from "i18n";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { hasAuthority } from "shared/authorization";
import { listReminderPages, Reminder, updateStatus } from "shared/network/reminder.api";
import ReminderModifyDialog from "views/Reminder/ReminderModifyDialog";

const ReminderDetails = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const locationUse = useLocation();
  const query = new URLSearchParams(locationUse.search);
  const id = query.get("id");

  const account = useSelector((state: RootState) => state?.authentication?.account);
  const selectedRelTenant = useSelector(
    (state: RootState) => state?.authentication?.selectedRelTenant,
  );
  const tenant = selectedRelTenant?.tenant;

  const [loading, setLoading] = useState(false);
  const [selectedModifyReminder, setSelectedModifyReminder] = useState<Reminder | null>(null);

  const reminderDetailsQuery = useQuery(["reminderDetailsQuery", id, tenant?.id], async () => {
    if (tenant?.id) {
      const { data } = await listReminderPages(0, 1, tenant?.id, `id=${id};`);
      return data?.page?.content?.[0];
    }
    return Promise.reject();
  });
  const selectedReminder = reminderDetailsQuery.data;

  async function statusModify(id: number, status: "DONE" | "REMOVED") {
    setLoading(true);
    try {
      await updateStatus(id, status, tenant?.id);
      queryClient.refetchQueries("reminderList");
      setSelectedModifyReminder(null);
      enqueueSnackbar(t("common:notification.save.success"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("common:notification.save.failure"), { variant: "error" });
    }
    setLoading(false);
  }

  return (
    <Container maxWidth="xl">
      <Card style={{ backgroundColor: "rgba(255, 255, 255, 0.6)", minWidth: 700 }}>
        <Loading open={loading || reminderDetailsQuery?.isFetching} />
        {selectedReminder && (
          <ReminderModifyDialog
            selectedReminder={selectedModifyReminder}
            setSelectedReminder={setSelectedModifyReminder}
          />
        )}

        {selectedReminder && (
          <Box width="100%" p={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pb={1}
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center">
                <Typography style={{ fontWeight: "bold", fontSize: 18 }}>
                  {selectedReminder?.user?.name}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <Typography style={{ paddingRight: 8 }}>
                  {selectedReminder?.when &&
                    format(new Date(selectedReminder?.when), "Pp", {
                      locale: supportedLocales[i18n.language],
                    })}
                </Typography>
                {selectedReminder?.status === "ACTIVE" && (
                  <Tooltip title={t("reminder.statusDoneTitle").toString()}>
                    <span>
                      <ConfirmationButton
                        variant="text"
                        size="small"
                        color="primary"
                        buttonType="ICON"
                        title={t("reminder.statusDoneTitle").toString()}
                        body={t("reminder.statusDoneBody").toString()}
                        onSubmit={async () => statusModify(Number(selectedReminder?.id), "DONE")}
                        style={{ color: COLORS.green }}
                      >
                        <Check />
                      </ConfirmationButton>
                    </span>
                  </Tooltip>
                )}
                {selectedReminder?.status === "ACTIVE" && (
                  <Tooltip title={t("reminder.statusRemoveTitle").toString()}>
                    <span>
                      <ConfirmationButton
                        variant="text"
                        size="small"
                        color="primary"
                        buttonType="ICON"
                        title={t("reminder.statusRemoveTitle").toString()}
                        body={t("reminder.statusRemoveBody").toString()}
                        onSubmit={async () => statusModify(Number(selectedReminder?.id), "REMOVED")}
                        style={{ color: COLORS.red }}
                      >
                        <Close />
                      </ConfirmationButton>
                    </span>
                  </Tooltip>
                )}
                {selectedReminder?.status === "REMOVED" && (
                  <Tooltip title={t("reminder.REMOVED").toString()}>
                    <Close style={{ color: COLORS.lightGrey }} />
                  </Tooltip>
                )}
                {selectedReminder?.status === "DONE" && (
                  <Tooltip title={t("reminder.DONE").toString()}>
                    <Check style={{ color: COLORS.lightGrey }} />
                  </Tooltip>
                )}
                {hasAuthority(account?.user, account?.permissions, selectedRelTenant, [
                  "EMPLOYEE_ADMIN",
                ]) && (
                  <Box>
                    <Tooltip title={t("common:button.modify").toString()}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setSelectedModifyReminder(selectedReminder);
                        }}
                      >
                        <Edit color="primary" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Box>
            <Divider />
            <Box display="flex" gridGap={16} pt={1.5}>
              {!!selectedReminder?.company && (
                <Box display="flex" alignItems="center" gridGap={8}>
                  <Tooltip title={t("emailLog.company").toString()}>
                    <Avatar style={{ height: 32, width: 32 }}>
                      <BusinessCenter fontSize="small" />
                    </Avatar>
                  </Tooltip>
                  <a
                    href={`/companies/list-all/company-details/details?id=${selectedReminder?.company?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Typography style={{ fontWeight: "bold" }}>
                      {selectedReminder?.company?.name}
                    </Typography>
                  </a>
                </Box>
              )}
              {selectedReminder?.issue && (
                <Box display="flex" alignItems="center" gridGap={8}>
                  <Tooltip title={t("issues.title").toString()}>
                    <Avatar style={{ height: 32, width: 32 }}>
                      <Note fontSize="small" />
                    </Avatar>
                  </Tooltip>
                  <Typography style={{ fontWeight: "bold" }}>
                    {selectedReminder?.issue?.name}
                  </Typography>
                </Box>
              )}
            </Box>
            {selectedReminder?.reminderMessage && (
              <Box
                style={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  borderRadius: 10,
                  marginTop: 12,

                  minHeight: "calc(100vh - 380px)",
                  maxHeight: "calc(100vh - 380px)",
                  overflow: "auto",
                }}
              >
                <div
                  style={{
                    padding: 16,
                    width: "100%",
                    height: "100%",
                    maxWidth: 1000,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: selectedReminder?.reminderMessage || "",
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Card>
    </Container>
  );
};

export default ReminderDetails;
